import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DigilockerCallbackService from "@/services/NPS/Stepper/APIs/digilockerCallback";
import navigationRoutes from "@/utils/navigationRoutes";
import { Spin } from "antd";
import { notify } from "@/common/components/common/Notification";

/**
 * DigilockerCallbackPage
 *
 * This page is deployed at: https://nps.universal-software.in/digilocker_callback
 * The parent app is at:     https://thefinease.universal-software.in
 *
 * ⚠️ IMPORTANT: Do NOT make API calls here.
 * The user's auth token lives in thefinease.universal-software.in's localStorage.
 * Since localStorage is domain-scoped, nps.universal-software.in has NO access to it.
 * Any authenticated API call made here would fail with 401.
 *
 * Instead, this page ONLY forwards the code/state to the parent window via
 * postMessage (using '*' since the origins differ). StepKYC.tsx (on thefinease)
 * receives the code and makes the API call with its own auth context.
 *
 * Standalone mode (no opener): the user opened the callback URL directly.
 * In that case, make the API call and navigate normally.
 */
const DigilockerCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (error) {
        console.error("❌ Digilocker authorization error:", error);
        if (window.opener) {
          // Forward error to parent — parent will show the error toast
          window.opener.postMessage(
            { type: "DIGILOCKER_CALLBACK", success: false, error },
            "*"
          );
          window.close();
        } else {
          notify.error(error);
          navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
        }
        return;
      }

      if (code && state) {
        if (window.opener) {
          // ✅ Popup mode: forward code to parent so IT can make the API call
          // with its own auth token (thefinease.universal-software.in localStorage).
          // Do NOT call digilockerCallback here — we have no auth in this domain.
          console.log("📨 Forwarding code to parent window via postMessage...");
          window.opener.postMessage(
            { type: "DIGILOCKER_CALLBACK", code, state, success: true },
            "*" // '*' required because parent is at a different origin
          );
          window.close();
        } else {
          // Standalone mode: user opened the callback URL directly (no parent window).
          // Make the API call ourselves.
          try {
            await DigilockerCallbackService.digilockerCallback(code);
            notify.success("Digilocker verified successfully!");
            navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
          } catch (err) {
            console.error("❌ Failed to exchange code:", err);
            notify.error("Failed to connect Digilocker. Please try again.");
            navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
          }
        }
      } else {
        console.error("❌ Missing code or state parameters");
        if (window.opener) {
          window.opener.postMessage(
            { type: "DIGILOCKER_CALLBACK", success: false },
            "*"
          );
          window.close();
        } else {
          notify.error("Missing required parameters.");
          navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
        }
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Spin size="large" />
        <p className="mt-4 text-lg text-white">Connecting to DigiLocker...</p>
      </div>
    </div>
  );
};

export default DigilockerCallbackPage;
